import { useEffect, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import type { IdentityCardCrop } from '@/types';
import { CARD_PHOTO_ASPECT } from '@/features/guide-identity/components/card/cardTokens';

interface IdentityPhotoCropDialogProps {
  open: boolean;
  /** Original (uncropped) image. */
  imageUrl: string;
  /** Previously saved crop, in pixels of the original — restores the framing. */
  initialCrop?: IdentityCardCrop;
  onCancel: () => void;
  onApply: (crop: IdentityCardCrop) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

function toCrop(area: Area): IdentityCardCrop {
  return {
    x: Math.max(0, Math.round(area.x)),
    y: Math.max(0, Math.round(area.y)),
    width: Math.max(1, Math.round(area.width)),
    height: Math.max(1, Math.round(area.height)),
  };
}

/** Crop/zoom the guide photo to the identity card's photo slot. */
export function IdentityPhotoCropDialog({ open, imageUrl, initialCrop, onCancel, onApply }: IdentityPhotoCropDialogProps) {
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);

  // Each opening starts from the saved crop (or centred).
  useEffect(() => {
    if (!open) return;
    setPosition({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
  }, [open, imageUrl]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="identity-crop-title">
      <DialogTitle id="identity-crop-title">Crop &amp; adjust photo</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag to reposition and use the slider to zoom. The highlighted frame is exactly what appears on the card.
        </Typography>
        <Box
          sx={{ position: 'relative', width: '100%', height: { xs: 340, sm: 420 }, bgcolor: 'grey.900', borderRadius: 2, overflow: 'hidden' }}
        >
          {open ? (
            <Cropper
              image={imageUrl}
              crop={position}
              zoom={zoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              aspect={CARD_PHOTO_ASPECT}
              initialCroppedAreaPixels={initialCrop}
              onCropChange={setPosition}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setArea(pixels)}
              objectFit="contain"
              showGrid
            />
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, px: 1 }}>
          <ZoomOutIcon color="action" aria-hidden />
          <Slider
            value={zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            onChange={(_, value) => setZoom(value as number)}
            aria-label="Zoom"
          />
          <ZoomInIcon color="action" aria-hidden />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!area} onClick={() => area && onApply(toCrop(area))}>
          Apply crop
        </Button>
      </DialogActions>
    </Dialog>
  );
}
